"use client";

import { useEffect } from "react";
import {
    clampTransform,
    newElementId,
    topZIndex,
    type SceneElement,
} from "@/features/lomdaEditorV2/lib/elements";
import { useEditorStoreApi } from "./context";

/**
 * Cross-scene clipboard for a copied/cut element. Module-level so it survives
 * scene switches and editor remounts.
 */
let elementClipboard: SceneElement | null = null;

/** Cascade each paste a little so successive copies don't stack exactly. */
const PASTE_OFFSET = 3;

function isTextEntryTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest("[data-rich-text-editor]") !== null
    );
}

function cloneForPaste(
    source: SceneElement,
    targetElements: SceneElement[]
): SceneElement {
    const clone = structuredClone(source);
    clone.id = newElementId();
    clone.zIndex = topZIndex(targetElements) + 1;
    clone.transform = clampTransform({
        ...clone.transform,
        x: clone.transform.x + PASTE_OFFSET,
        y: clone.transform.y + PASTE_OFFSET,
    });
    return clone;
}

/**
 * Cmd/Ctrl + C / X / V to copy, cut and paste the selected canvas element.
 * Paste targets the currently selected scene, so a copy can cross scenes.
 * Bubble-phase and scoped to C/X/V only, so it never touches other shortcuts
 * (undo/redo, arrows). Skipped while a text editor or input owns the native
 * clipboard. Mount once.
 */
export function useClipboardShortcuts() {
    const store = useEditorStoreApi();
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
            const k = e.key.toLowerCase();
            if (k !== "c" && k !== "x" && k !== "v") return;

            const state = store.getState();
            if (state.activeTextEditorId || isTextEntryTarget(e.target)) return;

            const sceneId = state.selectedSceneId;
            if (!sceneId) return;
            const scene = state.scenes.find((s) => s.id === sceneId);
            if (!scene) return;

            if (k === "v") {
                if (!elementClipboard) return;
                e.preventDefault();
                const clone = cloneForPaste(
                    elementClipboard,
                    scene.elements ?? []
                );
                elementClipboard = clone;
                state.addElement(sceneId, clone);
                return;
            }

            const sel = state.selection;
            if (sel.kind !== "element") return;
            const source = scene.elements?.find((x) => x.id === sel.elementId);
            if (!source) return;
            e.preventDefault();
            elementClipboard = structuredClone(source);
            if (k === "x") state.removeElement(sceneId, source.id);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [store]);
}
